import { makeStyles, Tooltip, Typography } from '@material-ui/core';
import { TitleTree, NodeTypeName } from '../../domain/domain';
import React from 'react';
import classnames from 'classnames';
import { stripTags } from '../../utils';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../reducers';
import { findCitationLabel } from './utils';

type Props = {
    nodeId: number
}

const useStyles = makeStyles(_ => ({
    nodeHasPendingApproval: {
        color: '#c44040' //'var(--base-lcs-red)'
    },
    unselectable: {
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        userSelect: 'none'
    },
    editField: {
        width: '100%'
    }
}));

function getWorkingCopyCitationPart(citation: string, citationPart: string, workingCopyCitation: string | undefined): string {
    if (!workingCopyCitation) return citation;

    const baseCitation = citation.replace(`(${citationPart})`, "");
    const workingCopyCitationPart = workingCopyCitation.replace(baseCitation, "");

    return workingCopyCitationPart;
}

const getHeading = (subTree: TitleTree) => {
    if (subTree.workingCopyHeading !== undefined) {
        if (!subTree.belowEditorialLevel) {
            if (subTree.citation === undefined) {
                return "";
            }
            let prefix = findCitationLabel(subTree.citation);
            return prefix !== undefined ? prefix : "";
        } else {
            return subTree.workingCopyHeading;
        }
    } else {
        if (!subTree.belowEditorialLevel) {
            if (subTree.citation === undefined) {
                return "";
            }
            let prefix = findCitationLabel(subTree.citation);
            return prefix !== undefined ? prefix : "";
        } else {
            return subTree.heading;
        }
    }
}

const findSubTreeById = (root: TitleTree | undefined, nodeId: number): TitleTree | undefined => {
    if (root === undefined) {
        return undefined;
    }
    if (root.nodeId === nodeId) {
        return root;
    }
    for (let i = 0; i < root.children.length; i++) {
        let found = findSubTreeById(root.children[i], nodeId);
        if (found !== undefined) {
            return found;
        }
    }
    return undefined;
}

const hasHeadingChanged = (subTree: TitleTree) =>
    subTree.workingCopyHeading && subTree.heading !== subTree.workingCopyHeading;

const hasCitationChanged = (subTree: TitleTree) =>
    subTree.workingCopyCitation && subTree.citation !== subTree.workingCopyCitation;

const renderNodeText = (subTree: TitleTree, classes: any) => {
    if (subTree.nodeType === NodeTypeName.SECTION) {
        if (subTree.subTreeHasUnapprovedChanges) {
            if (hasHeadingChanged(subTree) || hasCitationChanged(subTree)) {
                return <Typography variant='body1' className={classnames(classes.nodeHasPendingApproval, classes.unselectable)}>*{subTree.workingCopyCitation} {stripTags(getHeading(subTree))}</Typography>
            } else {
                return <Typography variant='body1' className={classnames(classes.nodeHasPendingApproval, classes.unselectable)}>*{subTree.citation} {stripTags(getHeading(subTree))}</Typography>
            }
        } else {
            return <Typography className={classes.unselectable} variant='body1'>{subTree.citation} {stripTags(getHeading(subTree))}</Typography>
        }
    } else if ([NodeTypeName.SUBSECTION, NodeTypeName.PARAGRAPH, NodeTypeName.SUBPARAGRAPH, NodeTypeName.SUB_SUBPARAGRAPH].includes(subTree.nodeType)) {
        if (subTree.subTreeHasUnapprovedChanges) {
            if (hasHeadingChanged(subTree) || hasCitationChanged(subTree)) {
                return <Typography variant='body1' className={classnames(classes.nodeHasPendingApproval, classes.unselectable)}>*{getWorkingCopyCitationPart(subTree.citation, subTree.citationPart, subTree.workingCopyCitation)}</Typography>
            } else {
                return <Typography variant='body1' className={classnames(classes.nodeHasPendingApproval, classes.unselectable)}>*{stripTags(getHeading(subTree))}</Typography>
            }
        } else {
            return <Typography className={classes.unselectable} variant='body1'>{stripTags(getHeading(subTree))}</Typography>
        }
    } else {
        if ((hasUnapprovedChildren(subTree) && subTree.subTreeHasUnapprovedChanges) || subTree.nodeHasChanges) {
            return <Typography variant='body1' className={classnames(classes.nodeHasPendingApproval, classes.unselectable)}>*{stripTags(getHeading(subTree))}</Typography>
        } else {
            return <Typography className={classes.unselectable} variant='body1'>{stripTags(getHeading(subTree))}</Typography>
        }
    }
}

const hasUnapprovedChildren = (root: TitleTree): boolean => {
    if (!root.children || root.children.length === 0) {
      return false;
    }

    return root.children.some(child => child.subTreeHasUnapprovedChanges);
  }

const renderNodeChangesAsTooltip = (subTree: TitleTree) => {
    let textParts = []

    if(hasUnapprovedChildren(subTree)){
        textParts.push("Change made to child node(s)");
    }
    if (subTree.subTreeHasUnapprovedChanges) {
        if(subTree.headingHasChanges){
            textParts.push("Change made to header/headnote");
        }
        if(subTree.workingCopyHasChanges){
            textParts.push("Change made to statutory content");
        }
        if(subTree.editorialHasChanges){
            textParts.push("Change made to editorial content");
        }
        if(subTree.workingCopyCitation && subTree.citation !== subTree.workingCopyCitation){
            textParts.push("Change made to citation");
        }
        if(subTree.lastApprovedDate == null){
            textParts.push("Unapproved: Initial Version.");
        }
    }
    if(textParts.length === 0){
        return "";
    }
    return (textParts.map((part, index) => {
        return <p key={index}>{part}</p>
    }));
}

const NodeTitle: React.FC<Props> = ({ nodeId }) => {

    const subTree = useSelector((app: ApplicationState) => findSubTreeById(app.comp.treeRoot, nodeId));
    const classes = useStyles();

    if (subTree === undefined) {
        return false;
    }

    return (<Tooltip title={renderNodeChangesAsTooltip(subTree)}>
        {renderNodeText(subTree, classes)}
    </Tooltip>)
}

export default NodeTitle;
